<!--
 * @Description: 无限循环轮播组件
 * @Author: Auto Generated
 * @Date: 2025-01-XX
-->
<template>
    <div class="w-full overflow-x-hidden relative isolate z-0">
        <button v-if="shouldShowArrows" class="absolute z-[1] left-0 top-[40%] left-arrow bg-black/40 rounded-full"
            :aria-label="t('common.previous')" @click="scrollLeft"></button>
        <button v-if="shouldShowArrows" class="absolute z-[1] right-0 top-[40%] right-arrow bg-black/40 rounded-full"
            :aria-label="t('common.next')" @click="scrollRight"></button>
        <div ref="scrollContainerRef"
            class="w-full relative z-0 flex flex-nowrap overflow-x-auto scroll-smooth scrollbar-hide" :class="[
                containerClass,
                enableSnap ? 'snap-x snap-mandatory md:snap-none' : '',
            ]" :style="{ gap: `${isMobile && enableSnap ? 0 : isMobile ? gap : 12}px` }" @mouseenter="pauseAutoplay"
            @mouseleave="resumeAutoplay" @scroll="handleScroll">
            <!-- 克隆的末尾元素（放在前面，用于向左滚动时的无缝循环） -->
            <div v-for="entry in cloneEndItems" :key="`clone-end-${getItemKey(entry.item, entry.index)}`"
                class="carousel-item-layer flex-shrink-0" :class="[
                    enableSnap ? 'snap-start md:snap-align-none' : '',
                    isMobile && enableSnap ? 'w-full min-w-full' : '',
                ]" @click="handleItemClick(entry.item, entry.index)">
                <slot :item="entry.item" :index="entry.index" :isClone="true" cloneType="end" />
            </div>

            <!-- 真实元素 -->
            <div v-for="(item, index) in items" :key="getItemKey(item, index)" :class="[
                'carousel-item-layer flex-shrink-0',
                enableSnap ? 'snap-start md:snap-align-none' : '',
                isMobile && enableSnap ? 'w-full min-w-full' : '',
            ]" @click="handleItemClick(item, index)">
                <slot :item="item" :index="index" :isClone="false" cloneType="real" />
            </div>

            <!-- 克隆的开头元素（放在后面，用于向右滚动时的无缝循环） -->
            <div v-for="entry in cloneStartItems" :key="`clone-start-${getItemKey(entry.item, entry.index)}`"
                class="carousel-item-layer flex-shrink-0" :class="[
                    enableSnap ? 'snap-start md:snap-align-none' : '',
                    isMobile && enableSnap ? 'w-full min-w-full' : '',
                ]" @click="handleItemClick(entry.item, entry.index)">
                <slot :item="entry.item" :index="entry.index" :isClone="true" cloneType="start" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, onMounted, nextTick, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// 响应式检测是否移动端
const isMobile = ref(false)

const updateIsMobile = () => {
    isMobile.value = window.innerWidth < 768 // md 断点
}

interface Props {
    // 数据列表
    items: any[]
    // 是否显示左右箭头
    showArrows?: boolean
    // 是否启用无缝循环（克隆 + 跳转实现）；关闭时为“普通横向列表”，可用于分页加载场景
    infinite?: boolean
    // 自动播放间隔（毫秒），0 表示不自动播放
    autoplayInterval?: number
    // 容器额外的 class
    containerClass?: string
    // 获取 item 的唯一 key 的函数
    getItemKey?: (item: any, index: number) => string | number
    // 卡片间距（px），默认 16
    gap?: number
    // 左右按钮（以及自动播放）每次滚动几个卡片，默认 1
    scrollStep?: number
    // 是否开启滚动捕捉（常用于全屏/单卡片模式）
    enableSnap?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    showArrows: true,
    infinite: true,
    autoplayInterval: 5000,
    containerClass: '',
    getItemKey: (item: any, index: number) => {
        // 默认使用 index，如果有 id 或 key 属性则使用
        return item?.id || item?.key || item?.video || index
    },
    gap: 16,
    scrollStep: 1,
    enableSnap: false,
})

const emit = defineEmits<{
    itemClick: [item: any, index: number]
    activeChange: [index: number]
    // 非 infinite 模式下：滚动到接近末尾时触发（用于外部加载下一页）
    nearEnd: [index: number]
}>()

// 计算是否显示箭头（移动端强制隐藏）
const shouldShowArrows = computed(() => props.showArrows && !isMobile.value)

const scrollContainerRef = ref<HTMLElement | null>(null)

// 循环滚动相关
let realContentWidth = 0 // 真实内容的宽度
let autoplayTimer: ReturnType<typeof setInterval> | null = null
let isAutoplayPaused = false // 是否暂停自动播放
let cloneContentWidth = 0 // 克隆区域宽度（仅克隆视口所需数量）
const cloneCount = ref(0)
let isJumping = false // 是否正在无动画跳转（防止 scroll 递归触发）
let isContainerVisible = true
let visibilityObserver: IntersectionObserver | null = null
let resizeTimer: ReturnType<typeof setTimeout> | null = null
let resizeHandler: (() => void) | null = null
let lastActiveIndex = -1
let hasEmittedNearEnd = false

const isInfiniteEnabled = computed(() => props.infinite && (props.items?.length || 0) > 1)

const cloneEndItems = computed(() => {
    if (!isInfiniteEnabled.value) return []
    const n = cloneCount.value
    const arr = props.items || []
    if (!n || arr.length === 0) return []
    const start = Math.max(0, arr.length - n)
    return arr.slice(start).map((item, i) => ({
        item,
        index: start + i,
    }))
})

const cloneStartItems = computed(() => {
    if (!isInfiniteEnabled.value) return []
    const n = cloneCount.value
    const arr = props.items || []
    if (!n || arr.length === 0) return []
    return arr.slice(0, n).map((item, i) => ({
        item,
        index: i,
    }))
})

const getEffectiveGap = () => {
    // 与模板 :style 的 gap 完全一致：移动端+snap→0，移动端→props.gap，PC→12
    if (isMobile.value && props.enableSnap) return 0
    if (isMobile.value) return props.gap
    return 12
}

// 获取 item 的唯一 key
const getItemKey = (item: any, index: number): string | number => {
    return props.getItemKey(item, index)
}

// 处理项目点击
const handleItemClick = (item: any, index: number) => {
    emit('itemClick', item, index)
}

const jumpTo = (newScrollLeft: number) => {
    if (!scrollContainerRef.value) return
    isJumping = true
    scrollContainerRef.value.style.scrollBehavior = 'auto'
    scrollContainerRef.value.scrollLeft = newScrollLeft
    requestAnimationFrame(() => {
        if (!scrollContainerRef.value) return
        scrollContainerRef.value.style.scrollBehavior = 'smooth'
        requestAnimationFrame(() => {
            isJumping = false
            updateActiveIndex()
        })
    })
}

const maybeEmitNearEnd = () => {
    if (!scrollContainerRef.value) return
    if (isInfiniteEnabled.value) return
    const maxScrollLeft =
        scrollContainerRef.value.scrollWidth -
        scrollContainerRef.value.clientWidth
    if (maxScrollLeft <= 0) return
    const buffer = Math.max(48, getCardWidth() * 4)
    const nearEnd = scrollContainerRef.value.scrollLeft >= maxScrollLeft - buffer
    if (nearEnd) {
        if (!hasEmittedNearEnd) {
            hasEmittedNearEnd = true
            emit('nearEnd', Math.max(0, lastActiveIndex))
        }
    } else {
        hasEmittedNearEnd = false
    }
}

// 初始化循环滚动
// targetIndex: 可选参数，指定初始化后滚动到的位置（用于 infinite 模式或需要强制对齐卡片时）
const initInfiniteScroll = (targetIndex?: number) => {
    if (!scrollContainerRef.value || props.items.length === 0) return

    nextTick(async () => {
        if (!scrollContainerRef.value) return

        const currentScrollLeft = scrollContainerRef.value.scrollLeft

        // 用第一个卡片测量宽度（克隆/真实一致）
        const firstChild =
            scrollContainerRef.value.firstElementChild as HTMLElement | null
        if (!firstChild) return

        const cardWidth = firstChild.offsetWidth
        const gap = getEffectiveGap()
        const cardFullWidth = cardWidth + gap
        const containerWidth = scrollContainerRef.value.clientWidth

        // 非 infinite 模式：不渲染克隆区，也不做循环跳转（用于分页加载）
        if (!isInfiniteEnabled.value) {
            cloneCount.value = 0
            cloneContentWidth = 0
            realContentWidth = props.items.length * cardFullWidth - gap
            // 追加数据时如果强制对齐到 index，会造成“轻微回弹/抖动”；
            // 因此：只有显式传入 targetIndex 才对齐，否则保持当前像素滚动位置。
            const targetScrollLeft =
                typeof targetIndex === 'number'
                    ? Math.max(
                        0,
                        Math.min(props.items.length - 1, targetIndex)
                    ) * cardFullWidth
                    : currentScrollLeft
            scrollContainerRef.value.style.scrollBehavior = 'auto'
            scrollContainerRef.value.scrollLeft = targetScrollLeft
            requestAnimationFrame(() => {
                if (scrollContainerRef.value) {
                    scrollContainerRef.value.style.scrollBehavior = 'smooth'
                    updateActiveIndex()
                    maybeEmitNearEnd()
                }
            })
            if (props.autoplayInterval > 0) startAutoplay()
            return
        }

        // 只克隆“填满视口 + 1”的数量，避免 DOM 变成 3x items
        const visibleCount = Math.ceil(containerWidth / cardFullWidth) + 1
        cloneCount.value = Math.min(
            props.items.length,
            Math.max(1, visibleCount)
        )
        // 克隆项进入 DOM 后再设置 scrollLeft，否则浏览器会按旧 scrollWidth 截断目标位置。
        await nextTick()
        if (!scrollContainerRef.value) return
        cloneContentWidth = cloneCount.value * cardFullWidth - gap
        realContentWidth =
            props.items.length * cardFullWidth - gap

        // 计算目标滚动位置
        // 如果指定了 targetIndex，滚动到该位置；否则滚动到起始位置
        const safeIndex = typeof targetIndex === 'number'
            ? Math.max(0, Math.min(props.items.length - 1, targetIndex))
            : 0
        const targetScrollLeft = cloneContentWidth + safeIndex * cardFullWidth

        // 先关闭平滑滚动，实现瞬间定位
        scrollContainerRef.value.style.scrollBehavior = 'auto'
        scrollContainerRef.value.scrollLeft = targetScrollLeft
        // 恢复平滑滚动
        requestAnimationFrame(() => {
            if (scrollContainerRef.value) {
                scrollContainerRef.value.style.scrollBehavior = 'smooth'
                updateActiveIndex()
            }
        })

        // 启动自动播放
        if (props.autoplayInterval > 0) {
            startAutoplay()
        }
    })
}

// 处理滚动事件，实现无缝跳转
const handleScroll = () => {
    if (!scrollContainerRef.value || isJumping || realContentWidth === 0) return

    if (!isInfiniteEnabled.value) {
        updateActiveIndex()
        maybeEmitNearEnd()
        return
    }

    const scrollLeft = scrollContainerRef.value.scrollLeft
    const realStart = cloneContentWidth
    const realEnd = cloneContentWidth + realContentWidth

    // 进入左侧克隆区域：跳到真实内容末尾对应位置
    if (scrollLeft <= realStart - 50) {
        const offset = scrollLeft
        // 等价：realEnd - (realStart - offset) => realContentWidth + offset
        jumpTo(realContentWidth + offset)
        return
    }

    // 进入右侧克隆区域：跳到真实内容起始对应位置
    if (scrollLeft >= realEnd + 50) {
        const offset = scrollLeft - realEnd
        jumpTo(realStart + offset)
        return
    }

    updateActiveIndex()
}

// 获取单个卡片的宽度
const getCardWidth = (): number => {
    if (!scrollContainerRef.value) return 0

    const firstChild = scrollContainerRef.value.firstElementChild as HTMLElement
    if (!firstChild) return 0

    return firstChild.offsetWidth + getEffectiveGap()
}

const getScrollAmount = (): number => {
    const cardWidth = getCardWidth()
    const step = Number.isFinite(props.scrollStep)
        ? Math.max(1, Math.floor(props.scrollStep))
        : 1
    return cardWidth * step
}

const updateActiveIndex = () => {
    if (!scrollContainerRef.value || props.items.length === 0) return
    const cardWidth = getCardWidth()
    if (cardWidth === 0 || realContentWidth === 0) return

    const rawScroll = scrollContainerRef.value.scrollLeft
    let normalized = rawScroll - cloneContentWidth
    if (realContentWidth > 0) {
        normalized =
            ((normalized % realContentWidth) + realContentWidth) %
            realContentWidth
    }

    const nextIndex = Math.max(
        0,
        Math.min(props.items.length - 1, Math.round(normalized / cardWidth))
    )

    if (nextIndex !== lastActiveIndex) {
        lastActiveIndex = nextIndex
        emit('activeChange', nextIndex)
    }
}

const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.value || props.items.length === 0) return
    const firstChild = scrollContainerRef.value.firstElementChild as HTMLElement
    if (!firstChild) return
    const cardWidth = firstChild.offsetWidth
    const cardFullWidth = cardWidth + getEffectiveGap()
    const safeIndex = Math.max(0, Math.min(props.items.length - 1, index))
    const targetLeft = cloneContentWidth + cardFullWidth * safeIndex

    scrollContainerRef.value.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
    })
}

// 向左滚动
const scrollLeft = () => {
    if (!scrollContainerRef.value || realContentWidth === 0) return

    const scrollAmount = getScrollAmount()
    const currentScroll = scrollContainerRef.value.scrollLeft
    const realStart = cloneContentWidth
    const realEnd = cloneContentWidth + realContentWidth

    // 非 infinite 模式：直接 clamp，不做循环跳转
    if (!isInfiniteEnabled.value) {
        const nextLeft = Math.max(0, currentScroll - scrollAmount)
        scrollContainerRef.value.scrollTo({
            left: nextLeft,
            behavior: 'smooth',
        })
        return
    }

    // 已到最左侧 或 smooth 目标将越过真实区域左边界 → 跳到末尾完成循环
    if (currentScroll <= realStart + 50 || currentScroll <= 10 || currentScroll - scrollAmount <= realStart) {
        const cardWidth = getCardWidth()
        jumpTo(realEnd - (cardWidth > 0 ? cardWidth : 0))
        return
    }

    scrollContainerRef.value.scrollTo({
        left: currentScroll - scrollAmount,
        behavior: 'smooth',
    })
}

// 向右滚动
const scrollRight = () => {
    if (!scrollContainerRef.value || realContentWidth === 0) return

    const scrollAmount = getScrollAmount()
    const currentScroll = scrollContainerRef.value.scrollLeft
    const realStart = cloneContentWidth
    const realEnd = cloneContentWidth + realContentWidth
    const maxScrollLeft =
        scrollContainerRef.value.scrollWidth -
        scrollContainerRef.value.clientWidth

    // 非 infinite 模式：直接 clamp，不做循环跳转
    if (!isInfiniteEnabled.value) {
        const nextLeft = Math.min(maxScrollLeft, currentScroll + scrollAmount)
        scrollContainerRef.value.scrollTo({
            left: nextLeft,
            behavior: 'smooth',
        })
        return
    }

    // 已到最右侧 或 smooth 目标将越过真实区域右边界 → 跳到开头完成循环
    if (currentScroll >= realEnd - 50 || currentScroll >= maxScrollLeft - 10 || currentScroll + scrollAmount >= realEnd) {
        const cardWidth = getCardWidth()
        jumpTo(realStart + (cardWidth > 0 ? cardWidth : 0))
        return
    }

    scrollContainerRef.value.scrollTo({
        left: currentScroll + scrollAmount,
        behavior: 'smooth',
    })
}

// 启动自动播放
const startAutoplay = () => {
    stopAutoplay() // 先清除之前的定时器

    if (props.autoplayInterval <= 0) return

    autoplayTimer = setInterval(() => {
        if (
            !isAutoplayPaused &&
            isContainerVisible &&
            scrollContainerRef.value &&
            !document.hidden
        ) {
            scrollRight()
        }
    }, props.autoplayInterval)
}

// 停止自动播放
const stopAutoplay = () => {
    if (autoplayTimer) {
        clearInterval(autoplayTimer)
        autoplayTimer = null
    }
}

// 暂停自动播放（鼠标悬停时）
const pauseAutoplay = () => {
    isAutoplayPaused = true
}

// 恢复自动播放（鼠标离开时）
const resumeAutoplay = () => {
    isAutoplayPaused = false
}

onMounted(() => {
    // 初始化移动端检测
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    // 初始化循环滚动
    initInfiniteScroll()

    // 离屏自动暂停轮播，避免多个轮播在后台持续触发渲染/合成
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
        visibilityObserver = new IntersectionObserver(
            (entries) => {
                isContainerVisible = entries.some((e) => e.isIntersecting)
            },
            { threshold: 0.1 }
        )
        if (scrollContainerRef.value) {
            visibilityObserver.observe(scrollContainerRef.value)
        }
    }

    // resize 重新计算克隆数量（节流）
    if (typeof window !== 'undefined') {
        resizeHandler = () => {
            if (resizeTimer) clearTimeout(resizeTimer)
            resizeTimer = setTimeout(() => {
                initInfiniteScroll()
            }, 150)
        }
        window.addEventListener('resize', resizeHandler)
    }
})

// 跨断点（PC <-> Mobile）或开启/关闭 snap 时，需要重新初始化宽度计算与 scrollLeft。
// 否则在 DevTools 切换设备尺寸时，会出现“样式变了但滚动位置/宽度仍按旧尺寸计算”的错位。
watch(
    [isMobile, () => props.enableSnap, () => props.infinite],
    () => {
        if (!scrollContainerRef.value || props.items.length === 0) return
        // 关闭自动播放，避免重置期间产生滚动干扰
        stopAutoplay()
        // 强制让下一次 init 重新计算宽度
        realContentWidth = 0
        hasEmittedNearEnd = false
        nextTick(() => {
            initInfiniteScroll()
        })
    },
    { flush: 'post' }
)

// 监听数据变化，重新初始化
watch(
    () => props.items.length,
    (newLen, oldLen) => {
        hasEmittedNearEnd = false
        if (newLen === 0) {
            // 数据清空
            stopAutoplay()
        } else if (oldLen === 0) {
            // 初次加载：滚动到起始位置
            initInfiniteScroll(0)
        } else if (newLen > oldLen) {
            // 追加数据：
            // - infinite 模式：保持 activeIndex（会对齐卡片）
            // - 非 infinite 模式（分页加载）：保持当前 scrollLeft（避免对齐导致抖动）
            initInfiniteScroll(isInfiniteEnabled.value ? lastActiveIndex : undefined)
        } else {
            // 数据减少：重新初始化到起始位置
            initInfiniteScroll(0)
        }
    }
)

onUnmounted(() => {
    stopAutoplay()
    visibilityObserver?.disconnect()
    visibilityObserver = null
    if (typeof window !== 'undefined' && resizeHandler) {
        window.removeEventListener('resize', resizeHandler)
        resizeHandler = null
    }
    if (resizeTimer) {
        clearTimeout(resizeTimer)
        resizeTimer = null
    }
    window.removeEventListener('resize', updateIsMobile)
})

defineExpose({
    scrollToIndex,
})
</script>

<style lang="less" scoped>
// H5 横向滑动时避免整列卡片重绘闪烁：将每个轮播项提升为独立合成层
.carousel-item-layer {
    transform: translateZ(0);
    backface-visibility: hidden;
}

.left-arrow,
.right-arrow {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    border-radius: 50%;
    background-image: url('~/assets/images/common/right-arrow.svg');
    background-size: 24px 24px;
    background-repeat: no-repeat;
    background-position: center;
    background-color: rgb(0 0 0 / 0.4);
    transition: transform 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: scale(1.1);
    }
}

.left-arrow {
    rotate: 180deg;
}

// 隐藏滚动条但保持滚动功能；H5 使用 touch 滚动以减轻滑动闪烁
.scrollbar-hide {

    // 隐藏 WebKit 浏览器的滚动条（Chrome, Safari, Edge）
    &::-webkit-scrollbar {
        display: none;
    }

    // 隐藏 Firefox 的滚动条
    scrollbar-width: none;
    // 隐藏 IE 和旧版 Edge 的滚动条
    -ms-overflow-style: none;
    // iOS 惯性滚动与合成优化，减少滑动时重绘
    -webkit-overflow-scrolling: touch;
}

// 防止视频层在部分浏览器合成层中“越级”覆盖全局弹窗
:deep(video) {
    z-index: 0 !important;
}
</style>
